import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Section,
  SectionInner,
  SectionHead,
  SectionHeading,
  SectionSubheading,
  StickerButton,
} from "../../globalStyles";
import {
  BANDS,
  ProductList,
  ProductRow,
  Content,
  TitleRow,
  AppIcon,
  Title,
  Meta,
  Subtitle,
  BigStat,
  BigStatValue,
  BigStatLabel,
  FactList,
  FactItem,
  Status,
  Action,
  Visual,
  Slide,
  SaasRow,
  SaasStatus,
} from "./ProductsSection.elements";

const ProductMedia = ({ images, alt, band }) => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <Visual className="visual" $band={band}>
      {images.map((src, i) => (
        <Slide
          key={src}
          src={src}
          alt={`${alt} ${i + 1}`}
          loading={i === 0 ? "eager" : "lazy"}
          $active={i === idx}
        />
      ))}
    </Visual>
  );
};

const ProductsSection = () => {
  const [language, setLanguage] = useState("ko");
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/en") setLanguage("en");
    else if (location.pathname === "/ko") setLanguage("ko");
    else setLanguage(localStorage.getItem("language") || "ko");

    const handleLanguageChange = (e) => setLanguage(e.detail);
    window.addEventListener("languageChange", handleLanguageChange);
    return () =>
      window.removeEventListener("languageChange", handleLanguageChange);
  }, [location.pathname]);

  const SM_IMAGES = [1, 2, 3, 4, 5, 6].map(
    (n) => `/products/student-maker/${n}.jpg`
  );
  const JS_IMAGES = [1, 2, 3, 4, 5].map((n) => `/products/job-seeker/${n}.jpg`);
  const YE_IMAGES = ["/products/yeonae/1.jpg"];
  const GALAXY_IMAGES = [1, 2, 3, 4, 5].map(
    (n) => `/products/galaxy/${n}.png`
  );

  const content = {
    ko: {
      heading: "바삭한소프트가 만드는 게임",
      sub: "참신한 소재와 깊은 몰입감으로 전 세계 유저의 일상에 스며듭니다.",
      products: [
        {
          key: "studentMaker",
          icon: "/products/student-maker-icon.jpg",
          images: SM_IMAGES,
          platform: "모바일",
          title: "수험생 키우기",
          subtitle: "수능 시뮬레이션 게임",
          big: { value: "150만 명", label: "글로벌 누적 가입자" },
          stats: [
            { prefix: "대만 앱스토어 게임 ", strong: "전체 1위", suffix: " (2025.12)" },
            {
              prefix: "2026년 5월 한 달간 글로벌(한국 제외) 신규 유저 ",
              strong: "230,000명",
              suffix: " 달성",
            },
          ],
          ctaLabel: "자세히 보기",
          url: "https://student-maker.com/ko",
        },
        {
          key: "jobSeeker",
          icon: "/products/job-seeker-icon.png",
          images: JS_IMAGES,
          platform: "모바일",
          title: "취준생 키우기",
          subtitle: "취업 시뮬레이션 게임",
          big: { value: "1위", label: "대한민국 앱스토어" },
          stats: [{ prefix: "2026년 6월 30일 ", strong: "런칭" }],
          ctaLabel: "자세히 보기",
          url: "https://jobmaker.basakansoft.com/",
        },
        {
          key: "yeonae",
          images: YE_IMAGES,
          platform: "모바일, PC",
          title: "연애혁명 키우기",
          subtitle: "인기 웹툰 IP 기반 스토리 육성 시뮬레이션",
          upcoming: true,
        },
        {
          key: "galaxy",
          images: GALAXY_IMAGES,
          platform: "모바일",
          title: "은하원정대",
          subtitle: "우주 전투 텍스트 로그라이크",
          upcoming: true,
        },
      ],
      saas: {
        title: "SaaS 솔루션",
        desc: "게임 스튜디오의 생산성을 높이는 올인원 개발·운영 솔루션",
        tag: "2027년 이내 공개 예정",
      },
      releaseLabel: "출시 예정",
    },
    en: {
      heading: "Games we make at Basakansoft",
      sub: "Original themes and deep immersion that weave into players' daily lives.",
      products: [
        {
          key: "studentMaker",
          icon: "/products/student-maker-icon.jpg",
          images: SM_IMAGES,
          platform: "Mobile",
          title: "Korean Student Simulator",
          subtitle: "A Korean college entrance exam simulation game",
          big: { value: "1.5M", label: "Global registered users" },
          stats: [
            { prefix: "", strong: "#1", suffix: " on the App Store, Taiwan (Dec 2025)" },
            {
              prefix: "New overseas users in May 2026 (excl. Korea) ",
              strong: "230,000",
            },
          ],
          ctaLabel: "Learn more",
          url: "https://student-maker.com/ko",
        },
        {
          key: "jobSeeker",
          icon: "/products/job-seeker-icon.png",
          images: JS_IMAGES,
          platform: "Mobile",
          title: "Hire Me",
          subtitle: "A job-hunting simulation game",
          big: { value: "#1", label: "App Store, Korea" },
          stats: [{ prefix: "Launched ", strong: "June 30, 2026" }],
          ctaLabel: "Learn more",
          url: "https://jobmaker.basakansoft.com/",
        },
        {
          key: "yeonae",
          images: YE_IMAGES,
          platform: "Mobile, PC",
          title: "Love Revolution Maker (working title)",
          subtitle: "A story-raising sim based on a hit webtoon IP",
          upcoming: true,
        },
        {
          key: "galaxy",
          images: GALAXY_IMAGES,
          platform: "Mobile",
          title: "Galaxy Expedition",
          subtitle: "A space combat text roguelike",
          upcoming: true,
        },
      ],
      saas: {
        title: "SaaS Solution",
        desc: "An all-in-one dev & ops solution boosting game studio productivity",
        tag: "Coming before 2027",
      },
      releaseLabel: "Coming soon",
    },
  };

  const c = content[language];

  return (
    <Section id="products">
      <SectionInner>
        <SectionHead>
          <SectionHeading>{c.heading}</SectionHeading>
          <SectionSubheading>{c.sub}</SectionSubheading>
        </SectionHead>

        <ProductList>
          {c.products.map((p) => {
            const band = BANDS[p.key];
            return (
              <ProductRow key={p.key}>
                <Content className="content">
                  <TitleRow>
                    {p.icon && <AppIcon src={p.icon} alt="" />}
                    <Title>{p.title}</Title>
                  </TitleRow>
                  <Meta>{p.platform}</Meta>
                  <Subtitle>{p.subtitle}</Subtitle>

                  {p.upcoming ? (
                    <Status>{c.releaseLabel}</Status>
                  ) : (
                    <>
                      <BigStat>
                        <BigStatValue>{p.big.value}</BigStatValue>
                        <BigStatLabel>{p.big.label}</BigStatLabel>
                      </BigStat>
                      <FactList>
                        {p.stats.map((s, idx) => (
                          <FactItem key={idx}>
                            {s.prefix}
                            <strong>{s.strong}</strong>
                            {s.suffix || ""}
                          </FactItem>
                        ))}
                      </FactList>
                      <Action>
                        <StickerButton
                          href={p.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {p.ctaLabel}
                        </StickerButton>
                      </Action>
                    </>
                  )}
                </Content>
                <ProductMedia images={p.images} alt={p.title} band={band} />
              </ProductRow>
            );
          })}
        </ProductList>

        <SaasRow style={{ marginTop: "var(--space-16)" }}>
          <div>
            <h3>{c.saas.title}</h3>
            <p>{c.saas.desc}</p>
          </div>
          <SaasStatus>{c.saas.tag}</SaasStatus>
        </SaasRow>
      </SectionInner>
    </Section>
  );
};

export default ProductsSection;
